import { useState } from "react";
import { useCreatePointsAdjustment } from "../hooks/usePoints";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";

interface Props {
  customerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Manually award (Credit) or deduct (Debit) loyalty points. Creates a Pending
 * entry that finance must approve — points are never silently adjusted.
 */
export const PointsAdjustDialog = ({ customerId, open, onOpenChange }: Props) => {
  const notification = useNotification();
  const mutation = useCreatePointsAdjustment();
  const [points, setPoints] = useState("");
  const [direction, setDirection] = useState<"Credit" | "Debit">("Credit");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");

  const reset = () => {
    setPoints("");
    setDirection("Credit");
    setReason("");
    setReference("");
  };

  const submit = () => {
    const value = Number(points);
    if (!Number.isInteger(value) || value <= 0) {
      notification.error("Points must be a whole number greater than zero.");
      return;
    }
    if (reason.trim().length < 10) {
      notification.error("Reason must be at least 10 characters.");
      return;
    }

    mutation
      .mutateAsync({
        customerId,
        points: value,
        direction,
        reason: reason.trim(),
        reference: reference.trim() || null,
      })
      .then(() => {
        notification.success("Points adjustment recorded. Pending finance approval.");
        reset();
        onOpenChange(false);
      })
      .catch(() => notification.error("Failed to record points adjustment."));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust loyalty points</DialogTitle>
          <DialogDescription>
            Award or deduct points. Held for finance approval before it affects the balance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Field>
            <FieldLabel>Direction <span className="text-destructive">*</span></FieldLabel>
            <Select value={direction} onValueChange={(v) => setDirection(v as typeof direction)} disabled={mutation.isPending}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Credit">Award (Credit)</SelectItem>
                <SelectItem value="Debit">Deduct (Debit)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>Points <span className="text-destructive">*</span></FieldLabel>
            <Input
              type="number"
              step="1"
              min={1}
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              disabled={mutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Reason <span className="text-destructive">*</span></FieldLabel>
            <Input
              placeholder="At least 10 characters"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={mutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Reference</FieldLabel>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} disabled={mutation.isPending} />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Recording..." : "Record adjustment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
